import React, { useContext } from 'react';
import TypeAhead from './Filters/TypeAhead';
import IconButton from '@mui/material/IconButton';
import Icon from './Icon';
import { ContextAri } from '../../contextsProperty/ContextAri';
import SearchSelect from './Filters/SearchSelect';
import DateRangeFilter from './Filters/DateRangeFilter';
import { losOptions } from './ariHelpers';
import TypeaheadErrorMessage from './Filters/TypeaheadErrorMessage';
import QualityFilter from './Filters/QualityFilter';
import { capitalize } from '../../../utils/usefulFunctions';
import WithTooltip from './WithTooltip';
import Collapse from '@mui/material/Collapse';
import Card from '../../../muiComponents/Card';
import MuiGrid from '../../../muiComponents/Grid';
import GridChild from '../../../muiComponents/GridChild';
import ValidityFilter from './Filters/ValidityFilter';
import useTranslate from '../../../customHooks/useTranslate';

const FiltersBar = () => {
    const {
        filtersOptions,
        setFilters,
        handleSearch,
        supplierHotels,
        filters,
        clearFilters,
        isOpenedFiltersBar,
        getOptions,
        isLoading,
        validationErrors,
    } = useContext(ContextAri);

    const translate = useTranslate();

    const [roomFilter, rateFilter, boardFilter] = filtersOptions;

    const {
        los,
        dateLastUpdateFrom,
        dateLastUpdateTo,
        code,
        idSupplierHotel,
        idSupplierHotelRoomType,
        idSupplierHotelRatePlan,
        idSupplierHotelMealPlan,
        dateFrom,
        dateTo,
        quality,
        validity,
    } = filters;

    const handlaChangeFilter = (e, option) => {
        const { name } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: option,
        }));
    };

    const handleChangeDate = ([from, to]) => {
        const { nameFrom, valueFrom } = from;
        const { nameTo, valueTo } = to;

        setFilters(prev => ({
            ...prev,
            [nameFrom]: valueFrom,
            [nameTo]: valueTo,
        }));
    };

    const hotelsTypeaheadValue = (() => {
        const noHotelSelected = !code && !idSupplierHotel;
        if (noHotelSelected) return null;
        return {
            Cyrenne: code,
            IdSupplierHotel: idSupplierHotel,
            MappingCode: filters.MappingCode,
            Name: filters.Name,
        };
    })();

    const noOptionsText = filter => {
        const { hasError, name } = filter;
        if (hasError) {
            return <TypeaheadErrorMessage onClick={() => getOptions(name)} />;
        }
        if (!code && !idSupplierHotel) return translate('ari_required_hotel');
        return 'No options';
    };

    const handleChangeQualityFilter = ({ target: { name, value } }) => {
        setFilters(prev => ({
            ...prev,
            [name]: value,
        }));
    };

    const getOptionLabel = option => `${option.Name} - ${option.MappingCode}`;

    return (
        <Collapse
            sx={{ minHeight: 'unset !important' }}
            in={isOpenedFiltersBar}
            unmountOnExit
        >
            <Card
                className='flex flex-between flex-align-center'
                style={{ gap: 10, padding: '20px', marginBottom: 20 }}
            >
                <MuiGrid spacing={2}>
                    <GridChild gridProps={{ xs: 12, sm: 6, md: 4 }}>
                        <TypeAhead
                            options={supplierHotels.hotels}
                            isSmallOwner={supplierHotels.isSmallOwner}
                            value={hotelsTypeaheadValue}
                            loading={supplierHotels.isLoading}
                            errorOnLoad={supplierHotels.errorMessage}
                        />
                    </GridChild>

                    <GridChild gridProps={{ xs: 12, sm: 6, md: 4 }}>
                        <DateRangeFilter
                            value={[dateFrom, dateTo]}
                            onChange={handleChangeDate}
                            nameFrom='dateFrom'
                            nameTo='dateTo'
                            label='date'
                            fromInputProps={{
                                error: !!validationErrors?.dateFrom,
                            }}
                            toInputProps={{ error: !!validationErrors?.dateTo }}
                        />
                    </GridChild>

                    <GridChild gridProps={{ xs: 12, sm: 6, md: 4 }}>
                        <SearchSelect
                            label='LoS'
                            options={losOptions}
                            optionTextKey='text'
                            onChange={handlaChangeFilter}
                            name='los'
                            valueKey='value'
                            value={los}
                        />
                    </GridChild>

                    <GridChild gridProps={{ xs: 12, sm: 6, md: 4 }}>
                        <SearchSelect
                            label='room'
                            options={roomFilter.options}
                            onChange={handlaChangeFilter}
                            name='idSupplierHotelRoomType'
                            value={idSupplierHotelRoomType}
                            loading={roomFilter.isLoading}
                            noOptionsText={noOptionsText(roomFilter)}
                            getOptionLabel={getOptionLabel}
                        />
                    </GridChild>

                    <GridChild gridProps={{ xs: 12, sm: 6, md: 4 }}>
                        <SearchSelect
                            label='rate'
                            options={rateFilter.options}
                            onChange={handlaChangeFilter}
                            name='idSupplierHotelRatePlan'
                            value={idSupplierHotelRatePlan}
                            loading={rateFilter.isLoading}
                            noOptionsText={noOptionsText(rateFilter)}
                            getOptionLabel={getOptionLabel}
                        />
                    </GridChild>

                    <GridChild gridProps={{ xs: 12, sm: 6, md: 4 }}>
                        <SearchSelect
                            label='meal_plan'
                            options={boardFilter.options}
                            onChange={handlaChangeFilter}
                            name='idSupplierHotelMealPlan'
                            value={idSupplierHotelMealPlan}
                            loading={boardFilter.isLoading}
                            noOptionsText={noOptionsText(boardFilter)}
                            getOptionLabel={getOptionLabel}
                        />
                    </GridChild>

                    <GridChild gridProps={{ xs: 12, sm: 6, md: 4 }}>
                        <DateRangeFilter
                            value={[dateLastUpdateFrom, dateLastUpdateTo]}
                            onChange={handleChangeDate}
                            nameFrom='dateLastUpdateFrom'
                            nameTo='dateLastUpdateTo'
                            label='last_updated'
                        />
                    </GridChild>

                    <GridChild gridProps={{ xs: 12, sm: 6, md: 4 }}>
                        <QualityFilter
                            value={quality}
                            label='availability'
                            name='quality'
                            onChange={handleChangeQualityFilter}
                        />
                    </GridChild>
                    <GridChild gridProps={{ xs: 12, sm: 6, md: 4 }}>
                        <ValidityFilter
                            value={validity}
                            label='validity'
                            name='validity'
                            onChange={handleChangeQualityFilter}
                        />
                    </GridChild>
                </MuiGrid>

                <div className='flex-column flex-align-center gap-8'>
                    <WithTooltip title={capitalize(translate('search'))}>
                        <IconButton
                            size='small'
                            disabled={isLoading}
                            onClick={() => handleSearch(1)}
                        >
                            <Icon iconName='Search' />
                        </IconButton>
                    </WithTooltip>

                    <WithTooltip title={translate('clear_filters')}>
                        <IconButton
                            size='small'
                            onClick={clearFilters}
                            disabled={isLoading}
                        >
                            <Icon iconName='SearchOff' />
                        </IconButton>
                    </WithTooltip>
                </div>
            </Card>
        </Collapse>
    );
};

export default FiltersBar;
